import { animate, DURATION, EASING, isReducedMotion } from './anim.js';
import { el } from './dom.js';

/**
 * Ecran de chargement : monogramme anime + journal de connexion.
 *
 * Il ne ment pas sur l'avancement : chaque etape est poussee par le code qui
 * l'execute reellement. En cas d'echec, il s'arrete sur un message lisible,
 * sans boucle de retry ni ecran blanc.
 */

export type BootStep = 'discord' | 'auth' | 'profile';

const STEP_LABELS: Readonly<Record<BootStep, string>> = {
  discord: 'Connexion à Discord…',
  auth: 'Authentification…',
  profile: 'Chargement du profil…',
};

const STEP_ORDER: readonly BootStep[] = ['discord', 'auth', 'profile'];

export interface BootScreen {
  readonly root: HTMLElement;
  readonly setStep: (step: BootStep) => void;
  readonly fail: (title: string, detail: string, options?: { readonly calm?: boolean }) => void;
  readonly destroy: () => void;
}

function monogram(): HTMLElement {
  // Logo purement CSS/SVG : aucun asset a charger, aucune requete reseau.
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 120 120');
  svg.setAttribute('aria-hidden', 'true');
  svg.classList.add('boot__mark-svg');

  const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  ring.setAttribute('cx', '60');
  ring.setAttribute('cy', '60');
  ring.setAttribute('r', '52');
  ring.setAttribute('class', 'boot__ring');

  const arc = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  arc.setAttribute('cx', '60');
  arc.setAttribute('cy', '60');
  arc.setAttribute('r', '52');
  arc.setAttribute('class', 'boot__arc');

  const pip = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  // Pique stylise, trace a la main : la marque du salon.
  pip.setAttribute(
    'd',
    'M60 30c0 12-18 18-18 30a11 11 0 0 0 16 10c-1 8-4 13-8 16h20c-4-3-7-8-8-16a11 11 0 0 0 16-10c0-12-18-18-18-30Z',
  );
  pip.setAttribute('class', 'boot__pip');

  svg.append(ring, arc, pip);
  return el('div', { class: 'boot__mark' }, [svg]);
}

export function createBoot(): BootScreen {
  const steps = new Map<BootStep, HTMLElement>();
  const list = el('ol', { class: 'boot__steps', attrs: { 'aria-live': 'polite' } });

  for (const step of STEP_ORDER) {
    const node = el('li', { class: 'boot__step', data: { state: 'pending' } }, [
      el('span', { class: 'boot__dot', attrs: { 'aria-hidden': 'true' } }),
      el('span', { class: 'boot__step-label', text: STEP_LABELS[step] }),
    ]);
    steps.set(step, node);
    list.appendChild(node);
  }

  const message = el('div', { class: 'boot__message', attrs: { hidden: 'hidden' } });

  const root = el('main', { class: 'screen screen--boot' }, [
    el('div', { class: 'boot' }, [
      monogram(),
      el('div', { class: 'boot__titles' }, [
        el('p', { class: 'boot__kicker', text: 'Salon privé' }),
        el('h1', { class: 'boot__title', text: 'Salon Minuit' }),
      ]),
      list,
      message,
      el('p', {
        class: 'boot__legal',
        text: 'Jetons virtuels sans valeur · aucun achat · aucun retrait',
      }),
    ]),
  ]);

  let current: BootStep | null = null;

  function setStep(step: BootStep): void {
    if (current === step) return;
    const index = STEP_ORDER.indexOf(step);
    STEP_ORDER.forEach((key, position) => {
      const node = steps.get(key);
      if (!node) return;
      node.dataset['state'] = position < index ? 'done' : position === index ? 'active' : 'pending';
    });
    current = step;

    const active = steps.get(step);
    if (active && !isReducedMotion()) {
      animate(active, [{ opacity: 0.4 }, { opacity: 1 }], {
        duration: DURATION.micro,
        easing: EASING.out,
      });
    }
  }

  function fail(title: string, detail: string, options: { readonly calm?: boolean } = {}): void {
    for (const node of steps.values()) {
      if (node.dataset['state'] === 'active') node.dataset['state'] = 'stopped';
    }
    root.dataset['state'] = options.calm === true ? 'idle' : 'error';

    message.hidden = false;
    message.dataset['tone'] = options.calm === true ? 'calm' : 'error';
    message.replaceChildren(
      el('p', { class: 'boot__message-title', text: title }),
      el('p', { class: 'boot__message-detail', text: detail }),
    );
    animate(message, [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], {
      duration: DURATION.base,
    });
  }

  return {
    root,
    setStep,
    fail,
    destroy: () => root.remove(),
  };
}
